import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { Fragment } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import ReceiptsTooltip from 'src/components/ReceiptsTooltip/ReceiptsTooltip'
import SvgChevronRight from 'src/components/Svgs/SvgChevronRight'
import { useTranslate } from 'src/localization/Trns'
import {
  rollupStatusFromAttachments,
  verificationFromMatchingStatuses,
} from 'src/models/receiptVerification.models'
import { TransactionListModel } from 'src/models/transaction.models'
import Lnk from 'src/navigation/Lnk'
import { transactionDetailUrl } from 'src/navigation/urls'
import { transactionsQueryKeys } from 'src/queries/queries.constants'
import { queryClient } from 'src/queryClient'
import { selectOrganizationReceiptMgmtEnabledFromState } from 'src/redux/selectors/data/organizations.selectors'
import { selectCalculatedBackgroundLocation } from 'src/redux/selectors/router.selectors'
import { css } from 'styled-components'

type TransactionsListSmallProps = {
  transactions: TransactionListModel[]
}

const TransactionsListSmall: React.FC<TransactionsListSmallProps> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()

  const { analytics } = useAnalyticsContext()
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const receiptManagementEnabled = useSelector(
    selectOrganizationReceiptMgmtEnabledFromState,
  )

  return (
    <div
      css={css`
        background-color: ${styleConstants.colors.gray_lightest};
        padding: 0px ${styleConstants.widths.cardPaddingXXSMOnly} 0px;
        border-bottom-left-radius: ${styleConstants.borderRadius.box};
        border-bottom-right-radius: ${styleConstants.borderRadius.box};
      `}
    >
      {props.transactions.map((transaction, i) => (
        <Fragment key={i}>
          <Lnk
            to={{
              pathname: transactionDetailUrl(transaction.id),
              search: '',
              hash: '',
              state: {},
            }}
            css={css`
              display: flex;
              justify-content: space-between;
              padding: 20px 0px;
              overflow: hidden;
            `}
            ariaLabelSource={'Transaction Details'}
            onClick={(): void => {
              if (transaction.isSplit) {
                queryClient.removeQueries({
                  queryKey: transactionsQueryKeys.detail(transaction.id),
                })
              }
              analytics?.track(
                'transactions - visit transaction with ' +
                  transaction.status.toLowerCase() +
                  ' status - list item press',
                {
                  transactionStatus: transaction.status,
                  intent: 'visit transaction',
                  interaction: 'list item press',
                  screen: calculatedBackgroundLocation.pathname,
                  transactionId: transaction.id,
                },
              )
            }}
          >
            <div
              css={css`
                overflow: hidden;
                display: flex;
                flex-direction: column;
                flex-grow: 2;
              `}
            >
              <div
                css={css`
                  display: flex;
                  justify-content: space-between;
                `}
              >
                <span
                  css={css`
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    ${getFontCss(
                      14,
                      themeConstants.colors.font_primary,
                      styleConstants.fontFamilies.medium,
                    )};
                    ${transaction.isPending &&
                    css`
                      ${getFontCss(
                        14,
                        styleConstants.colors.gray_dark,
                        styleConstants.fontFamilies.medium,
                      )};
                    `}
                  `}
                >
                  {transaction.merchantNameOrUnknown}
                </span>
                <span
                  css={css`
                    ${getFontCss(
                      14,
                      themeConstants.colors.font_primary,
                      styleConstants.fontFamilies.regular,
                    )};
                    ${transaction.isPending &&
                    css`
                      ${getFontCss(
                        14,
                        styleConstants.colors.gray_dark,
                        styleConstants.fontFamilies.regular,
                      )};
                    `}
                    ${transaction.isDeclined &&
                    css`
                      ${getFontCss(
                        14,
                        styleConstants.colors.red,
                        styleConstants.fontFamilies.regular,
                      )};
                    `}
                  ${transaction.isCredit &&
                    css`
                      ${getFontCss(
                        14,
                        themeConstants.colors.highlight,
                        styleConstants.fontFamilies.regular,
                      )};
                    `}
                  `}
                >
                  {transaction.clearingOrAuthLabel()}
                </span>
              </div>
              <div
                css={css`
                  display: flex;
                  justify-content: space-between;
                `}
              >
                <span
                  css={css`
                    ${getFontCss(
                      12,
                      styleConstants.colors.gray_darkest,
                      styleConstants.fontFamilies.regular,
                    )};
                    ${transaction.isPending &&
                    css`
                      ${getFontCss(
                        12,
                        styleConstants.colors.gray_dark,
                        styleConstants.fontFamilies.regular,
                      )};
                    `}
                  `}
                >
                  {transaction.mccGroupIfKnownMerchant}
                </span>
                <span
                  css={css`
                    display: flex;
                  `}
                >
                  {transaction.isDeclined && (
                    <span
                      css={css`
                        ${getFontCss(
                          12,
                          styleConstants.colors.red,
                          styleConstants.fontFamilies.regular,
                        )};
                      `}
                    >
                      {translate('Declined')}
                    </span>
                  )}
                  {transaction.isCredit && (
                    <span
                      css={css`
                        ${getFontCss(
                          12,
                          themeConstants.colors.highlight,
                          styleConstants.fontFamilies.regular,
                        )};
                      `}
                    >
                      {translate('Credit')}
                    </span>
                  )}
                  {transaction.attachmentsCount !== undefined &&
                    transaction.attachmentsCount > 0 && (
                      <ReceiptsTooltip
                        count={transaction.attachmentsCount}
                        placement={'right'}
                        transactionId={transaction.id}
                        verificationStatus={
                          transaction.receiptMatchingStatus ??
                          rollupStatusFromAttachments(
                            transaction.attachments,
                          ) ??
                          verificationFromMatchingStatuses(
                            transaction.receiptMatchingStatuses,
                          )?.status
                        }
                        verificationReasons={
                          verificationFromMatchingStatuses(
                            transaction.receiptMatchingStatuses,
                          )?.reasons
                        }
                      />
                    )}
                  {receiptManagementEnabled &&
                    transaction.attachmentsCount === 0 &&
                    transaction.receiptRequired && (
                      <ReceiptsTooltip placement={'right'} isMissing />
                    )}
                </span>
              </div>
            </div>
            <div
              css={css`
                height: 18px;
              `}
            >
              <SvgChevronRight
                css={css`
                  transform-origin: top right;
                  transform: scale(calc(18 / 24));
                `}
              />
            </div>
          </Lnk>
        </Fragment>
      ))}
    </div>
  )
}

export default TransactionsListSmall
